import { useEffect, useState } from "react";
import { api, fmtDate, fmtTime, type Customer, type CustomerStage, type CustomerWebapp } from "../api";
import { ActionMenu } from "../components/ActionMenu";
import { SearchBox, matches } from "../components/SearchBox";

// Onboarding funnel, in the order a customer moves through it.
const STAGES: { key: CustomerStage; label: string; hint: string; tone: "" | "ok" | "bad" }[] = [
  {
    key: "email_unverified",
    label: "email not verified",
    hint: "Signed up but never clicked the verification mail, so they cannot log in.",
    tone: "bad",
  },
  {
    key: "no_webapp",
    label: "no webapp",
    hint: "Logged in but never created a webapp.",
    tone: "bad",
  },
  {
    key: "not_integrated",
    label: "never called API",
    hint: "Has a webapp but no OTP was ever issued with its API key.",
    tone: "bad",
  },
  {
    key: "no_verification",
    label: "no verification yet",
    hint: "Issues OTPs through /auth/init, but no end-user ever completed one.",
    tone: "bad",
  },
  { key: "active", label: "active", hint: "At least one end-user verification.", tone: "ok" },
];
const STAGE_BY_KEY = Object.fromEntries(STAGES.map((st) => [st.key, st]));

function stageDetail(cu: Customer): string {
  const f = cu.funnel;
  switch (cu.stage) {
    case "email_unverified":
      return `${f.verify_mails} verify mail${f.verify_mails === 1 ? "" : "s"} sent` +
        (f.last_verify_mail_at ? `, last ${fmtDate(f.last_verify_mail_at)}` : "");
    case "no_webapp":
      return cu.last_login_at ? `last login ${fmtDate(cu.last_login_at)}` : "never logged in";
    case "not_integrated":
      return `${f.webapps_ever} webapp${f.webapps_ever === 1 ? "" : "s"}, first ${fmtDate(f.first_webapp_at)}`;
    case "no_verification":
      return `${f.otp_issued} OTP issued, 0 verified` + (f.last_otp_day ? `, last ${f.last_otp_day}` : "");
    case "active":
      return `${f.otp_verified} verified` + (f.last_otp_day ? `, last ${f.last_otp_day}` : "");
  }
}

export function CustomersTab() {
  const [items, setItems] = useState<Customer[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [openWebapps, setOpenWebapps] = useState<Customer | null>(null);
  const [stageFilter, setStageFilter] = useState<CustomerStage | null>(null);
  const [suspending, setSuspending] = useState<Customer | null>(null);
  const [q, setQ] = useState("");

  async function reload() {
    try {
      const r = await api.listCustomers();
      setItems(r.items);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "load_failed");
    }
  }
  useEffect(() => {
    reload();
  }, []);

  async function reinstate(cu: Customer) {
    if (!confirm(`Lift the suspension on ${cu.email}? Their webapps start answering again at once.`)) return;
    try {
      await api.reinstateCustomer(cu.id);
      reload();
    } catch (e) {
      alert(e instanceof Error ? e.message : "reinstate_failed");
    }
  }

  async function remove(cu: Customer) {
    if (!confirm(`Delete customer ${cu.email}? This cannot be undone.`)) return;
    try {
      await api.deleteCustomer(cu.id);
      reload();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "delete_failed";
      alert(msg === "has_webapps" ? "Customer still owns webapps — delete those first." : msg);
    }
  }

  const visible = (items ?? []).filter(
    (cu) => (!stageFilter || cu.stage === stageFilter) && matches(q, cu.email, cu.phone),
  );

  return (
    <>
      <div className="panel">
        <div className="panel-head">
          <h2>
            Customers
            {items && (
              <span className="count">
                {visible.length === items.length ? items.length : `${visible.length} / ${items.length}`}
              </span>
            )}
          </h2>
          <div className="spacer" />
          <SearchBox value={q} onChange={setQ} placeholder="Search email or phone" />
        </div>
        {err && <div className="error">{err}</div>}
        {items && items.length > 0 && (
          <div className="chips">
            {STAGES.map((st) => {
              const n = items.filter((cu) => cu.stage === st.key).length;
              const on = stageFilter === st.key;
              return (
                <button
                  key={st.key}
                  type="button"
                  className={`chip ${on ? "active" : ""}`}
                  title={st.hint}
                  onClick={() => setStageFilter(on ? null : st.key)}
                >
                  {st.label} <span className="n">{n}</span>
                </button>
              );
            })}
          </div>
        )}
        {items === null ? (
          <div className="empty">Loading…</div>
        ) : items.length === 0 ? (
          <div className="empty">No customers yet.</div>
        ) : visible.length === 0 ? (
          <div className="empty">No customers match.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Stage</th>
                  <th>Phone</th>
                  <th className="r">Apps</th>
                  <th>Credits</th>
                  <th>Joined</th>
                  <th>Last login</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((cu) => {
                  const low = cu.credits_remaining <= 0;
                  const st = STAGE_BY_KEY[cu.stage];
                  return (
                    <tr key={cu.id} className={cu.suspended_at ? "dim" : ""}>
                      <td className="primary" data-label="Customer">
                        <div className="main">{cu.email}</div>
                        {(!cu.email_verified || !cu.agreement_accepted_at || cu.suspended_at) && (
                          <div className="pills">
                            {!cu.email_verified && <span className="pill bad">unverified</span>}
                            {!cu.agreement_accepted_at && (
                              <span
                                className="pill bad"
                                title={`Has not accepted user agreement ${cu.agreement_version}; portal is blocked until they do`}
                              >
                                agreement not accepted
                              </span>
                            )}
                            {cu.suspended_at && (
                              <span
                                className="pill bad"
                                title={`Suspended ${fmtTime(cu.suspended_at)}${cu.suspended_by ? ` by ${cu.suspended_by}` : ""}${cu.suspended_reason ? `: ${cu.suspended_reason}` : ""}`}
                              >
                                suspended
                              </span>
                            )}
                          </div>
                        )}
                        {cu.suspended_at && cu.suspended_reason && (
                          <div className="sub">{cu.suspended_reason}</div>
                        )}
                      </td>
                      <td data-label="Stage">
                        <span className={`pill ${st?.tone ?? ""}`} title={st?.hint}>
                          {st?.label ?? cu.stage}
                        </span>
                        <div className="sub">{stageDetail(cu)}</div>
                      </td>
                      <td data-label="Phone" className="nowrap">
                        {cu.phone ? <span className="mono">{cu.phone}</span> : <span className="muted">—</span>}
                      </td>
                      <td data-label="Apps" className="r mono">{cu.webapp_count}</td>
                      <td data-label="Credits" className="nowrap">
                        <span className="mono">
                          {cu.consumed_credits} / {cu.credit_allowance}
                        </span>
                        <div className={`sub ${low ? "bad-text" : ""}`}>{cu.credits_remaining} left</div>
                      </td>
                      <td data-label="Joined" className="nowrap" title={fmtTime(cu.created_at)}>
                        {fmtDate(cu.created_at)}
                      </td>
                      <td data-label="Last login" className="nowrap">{fmtTime(cu.last_login_at)}</td>
                      <td className="actions" data-label="Actions">
                        <div className="action-group">
                          <button className="secondary sm" onClick={() => setEditing(cu)}>
                            Credits
                          </button>
                          <ActionMenu
                            title={cu.email}
                            items={[
                              { label: "Webapps", onClick: () => setOpenWebapps(cu) },
                              { label: "Edit credits", onClick: () => setEditing(cu) },
                              cu.suspended_at
                                ? { label: "Reinstate", onClick: () => reinstate(cu), sep: true }
                                : { label: "Suspend…", onClick: () => setSuspending(cu), danger: true, sep: true },
                              { label: "Delete", onClick: () => remove(cu), danger: true },
                            ]}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {items && items.length > 0 && (
          <p className="note">
            OTPs that lapsed without the webapp polling them were not counted before this
            release, so some "never called API" accounts may in fact have tried.
          </p>
        )}
      </div>

      {editing && (
        <EditCustomerModal
          customer={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}

      {suspending && (
        <SuspendCustomerModal
          customer={suspending}
          onCancel={() => setSuspending(null)}
          onDone={() => {
            setSuspending(null);
            reload();
          }}
        />
      )}

      {openWebapps && (
        <CustomerWebappsModal customer={openWebapps} onClose={() => setOpenWebapps(null)} />
      )}
    </>
  );
}

function SuspendCustomerModal({
  customer,
  onCancel,
  onDone,
}: {
  customer: Customer;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.suspendCustomer(customer.id, reason.trim());
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "suspend_failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Suspend — {customer.email}</h3>
        <p className="muted">
          Takes effect immediately and only an admin can lift it. Every webapp of this account gets
          403 account_suspended from the API, in-flight OTPs are failed, and the portal shows a
          suspension notice: no webapp changes, no credit purchases, no self-service account
          deletion. Data export and logout stay available.
        </p>
        <label>
          Reason <span className="muted">(internal, not shown to the customer)</span>
        </label>
        <textarea
          rows={3}
          value={reason}
          maxLength={1000}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. used on an illegal betting site (example.com), §4"
        />
        {err && <div className="error">{err}</div>}
        <div className="modal-foot">
          <button type="button" className="secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="danger" disabled={busy || !reason.trim()}>
            {busy ? "Suspending…" : "Suspend account"}
          </button>
        </div>
      </form>
    </div>
  );
}

function EditCustomerModal({
  customer,
  onCancel,
  onSaved,
}: {
  customer: Customer;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [trial, setTrial] = useState(String(customer.trial_credits));
  const [paid, setPaid] = useState(String(customer.paid_credits));
  const [verified, setVerified] = useState(customer.email_verified);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.updateCustomer(customer.id, {
        trial_credits: Number(trial) || 0,
        paid_credits: Number(paid) || 0,
        email_verified: verified,
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "update_failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Credits — {customer.email}</h3>
        <p className="muted">
          Spent {customer.consumed_credits} of {customer.credit_allowance} granted
          ({customer.credits_remaining} remaining). One verified login = one credit.
        </p>
        <label>
          Trial credits <span className="muted">(one-time free allowance)</span>
        </label>
        <input type="number" min={0} value={trial} onChange={(e) => setTrial(e.target.value)} />
        <label>
          Paid credits <span className="muted">(purchased + manual top-ups)</span>
        </label>
        <input type="number" min={0} value={paid} onChange={(e) => setPaid(e.target.value)} />
        <label className="check" style={{ marginTop: 14 }}>
          <input
            type="checkbox"
            checked={verified}
            onChange={(e) => setVerified(e.target.checked)}
          />
          <span>Email verified</span>
        </label>
        {err && <div className="error">{err}</div>}
        <div className="modal-foot">
          <button type="button" className="secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

function CustomerWebappsModal({
  customer,
  onClose,
}: {
  customer: Customer;
  onClose: () => void;
}) {
  const [items, setItems] = useState<CustomerWebapp[] | null>(null);
  useEffect(() => {
    api.customerWebapps(customer.id).then((r) => setItems(r.items));
  }, [customer.id]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal wide" onClick={(e) => e.stopPropagation()}>
        <h3>{customer.email} — webapps</h3>
        {items === null ? (
          <div className="empty">Loading…</div>
        ) : items.length === 0 ? (
          <div className="empty">No webapps.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Webapp</th>
                  <th>Status</th>
                  <th className="r">Verified</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {items.map((w) => (
                  <tr key={w.id}>
                    <td className="primary" data-label="Webapp">
                      <div className="main">{w.name}</div>
                      <div className="sub mono">{w.code}</div>
                    </td>
                    <td data-label="Status">
                      {w.deleted ? (
                        <span className="pill bad">deleted</span>
                      ) : (
                        <span className={`pill ${w.active ? "ok" : "bad"}`}>
                          {w.active ? "active" : "disabled"}
                        </span>
                      )}
                    </td>
                    <td data-label="Verified" className="r mono">
                      {w.verified_count}
                      {w.unlimited ? " / ∞" : ""}
                    </td>
                    <td data-label="Created" className="nowrap">{fmtDate(w.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="modal-foot">
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
